/**
 * OnboardingTour — First-time user guided walkthrough
 * 7-step modal carousel introducing ProfitSpot's core features
 * Shows once per user account (tracked in localStorage, keyed by user email)
 */
import React, { useState, useEffect } from 'react';
import { useNavigate as useRouterNavigate } from 'react-router-dom';
import { X, ChevronRight, ChevronLeft, BarChart3, FlaskConical, Wallet, Users, Bell, Compass, Gauge } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const STEPS = [
  {
    id: 'welcome',
    icon: Compass,
    iconColor: '#00ff88',
    title: 'Welcome to ProfitSpot',
    subtitle: 'Your DeFi LP intelligence hub',
    description: 'ProfitSpot helps you find the best liquidity pool opportunities, simulate real profits before investing, track your positions, and follow where institutional money is moving.',
    highlight: null,
    tip: 'Everything you see is built for one goal: helping you maximize real DeFi returns while minimizing risk.',
    navigate: null,
  },
  {
    id: 'yield_explorer',
    icon: BarChart3,
    iconColor: '#00d4ff',
    title: 'Yield Explorer',
    subtitle: 'Browse 6,000+ live DeFi pools',
    description: 'The Yield Explorer aggregates live data from every major DeFi protocol — Uniswap, Curve, Aave, Aerodrome and more — across 10+ chains. Filter by chain, IL risk, protocol, and TVL to find the perfect pool for your strategy.',
    highlight: 'explore',
    steps: [
      'Click "Yield Explorer" in the left sidebar',
      'Use filters (chain, IL risk, TVL) to narrow results',
      'Click the flask icon (⚗️) on any pool card to simulate it instantly',
    ],
    tip: 'Free accounts see the top 25 curated pools. Pro+ users can search all 6,000+ pools.',
    navigate: '/explore',
  },
  {
    id: 'simulator',
    icon: FlaskConical,
    iconColor: '#ffa502',
    title: 'Simulator Labs',
    subtitle: 'Know your real profit before you invest',
    description: 'Most DeFi dashboards show Gross APY — the headline number that ignores gas fees, slippage, and impermanent loss. Simulator Labs shows your TRUE net profit after all costs. Use Scenario Presets to instantly model 5 common DeFi strategies.',
    highlight: 'simulator',
    steps: [
      'Click "Simulator Labs" in the sidebar',
      'Try a preset: Stable Pair, Blue Chip LP, or Degen Mode',
      'Adjust APY, chain, TVL tier, and holding period',
      'Watch the Break-even Chart update in real time',
    ],
    tip: 'Hit "Auto-Fill Market Rate" to load the current live APY for your selected chain with one click.',
    navigate: '/simulator',
  },
  {
    id: 'forecast_dial',
    icon: Gauge,
    iconColor: '#8b5cf6',
    title: 'Forecast Dial',
    subtitle: 'Monte Carlo yield predictions',
    description: 'The Forecast Dial runs 1,000 simulations using advanced financial modeling to predict your yield outcomes. See the probability of profit, best/worst case scenarios, and a ProfitSpot Score (0-100) that tells you how good an opportunity really is.',
    highlight: 'forecast',
    steps: [
      'Click "Forecast Dial" in the sidebar',
      'Search for any pool or enter details manually',
      'View the dial zone: Optimal (cyan), Favorable (green), Caution (yellow), Danger (red)',
      'Check probability of profit and best/worst case ROI scenarios',
    ],
    tip: 'The Forecast Dial is the "windshield" (what might happen next), while the Backtester is the "rear-view mirror" (what already happened). Use both together!',
    navigate: '/forecast',
  },
  {
    id: 'portfolio',
    icon: Wallet,
    iconColor: '#00ff88',
    title: 'Portfolio Tracker',
    subtitle: 'See every LP position in one view',
    description: 'Paste any Ethereum or L2 wallet address to instantly see all active LP positions, their current IL, profit/loss, and security scores. Pro users get AI-powered "Should I Exit?" recommendations for each position.',
    highlight: 'portfolio',
    steps: [
      'Click "Portfolio Tracker" in the sidebar',
      'Paste any wallet address (yours or any public wallet)',
      'Click a position card to expand and see the IL Calculator',
      'Pro users: click "AI Exit Analysis" for an instant recommendation',
    ],
    tip: 'You can analyze ANY public wallet — great for researching how successful LPs manage their positions.',
    navigate: '/portfolio',
  },
  {
    id: 'whale_tracker',
    icon: Users,
    iconColor: '#00d4ff',
    title: 'Whale Tracker',
    subtitle: 'Follow institutional smart money',
    description: 'ProfitSpot tracks 10 institutional DeFi wallets (Jump Trading, Wintermute, Coinbase Custody, and more) in real time via Moralis. See where $1M+ wallets are deploying capital — before the market notices.',
    highlight: 'whales',
    steps: [
      'Click "Whale Tracker" in the sidebar (Pro feature)',
      'Click any whale card to see full portfolio breakdown',
      'Click "Get Insight" for AI strategy analysis of that wallet',
      'Cross-reference whale positions with Yield Explorer',
    ],
    tip: 'When multiple whales enter the same protocol in the same week — that\'s a high-conviction signal.',
    navigate: '/whales',
  },
  {
    id: 'alerts',
    icon: Bell,
    iconColor: '#ffa502',
    title: 'Smart Yield Alerts',
    subtitle: 'Never miss a DeFi opportunity',
    description: 'Set threshold alerts for APY spikes, IL warnings, and whale movements. Get notified instantly via Email or Telegram when your watched pools hit your targets — without staring at dashboards all day.',
    highlight: 'alerts',
    steps: [
      'Click "Alert Settings" in the sidebar (Pro feature)',
      'Set minimum APY threshold (e.g., alert me when ETH/USDC > 30%)',
      'Add your email and optional Telegram bot for instant notifications',
      'Save preferences — alerts run 24/7 automatically',
    ],
    tip: 'Combine alerts with the Whale Tracker: if a whale enters a pool AND APY spikes, that\'s your signal to investigate.',
    navigate: '/alert-preferences',
  },
];

const STORAGE_KEY = 'profitspot_onboarding_v1';

export default function OnboardingTour() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useRouterNavigate();
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);
  const [neverShow, setNeverShow] = useState(true); // pre-checked: default saves on close

  // Build a user-specific key so each account gets the tour fresh
  const storageKey = user?.id
    ? `${STORAGE_KEY}_${user.id}`
    : user?.email
    ? `${STORAGE_KEY}_${user.email}`
    : STORAGE_KEY;

  useEffect(() => {
    // Show tour on first visit for this specific user (or guest)
    const done = localStorage.getItem(storageKey);
    if (!done) {
      // Small delay so the page settles before the modal appears
      const timer = setTimeout(() => setVisible(true), 1400);
      return () => clearTimeout(timer);
    }
  }, [storageKey, isAuthenticated]); // Re-check when auth state changes

  const finish = () => {
    if (neverShow) localStorage.setItem(storageKey, 'true');
    setVisible(false);
  };

  if (!visible) return null;

  const current = STEPS[step];
  const isFirst = step === 0;
  const isLast = step === STEPS.length - 1;
  const Icon = current.icon;

  const goToStep = (nextIndex) => {
    const next = STEPS[nextIndex];
    setStep(nextIndex);
    if (next?.navigate) navigate(next.navigate);
  };

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div
        className="bg-[#0d0d1a] border border-gray-700/60 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden"
        data-testid="onboarding-tour"
      >
        {/* Progress bar */}
        <div className="h-1 bg-gray-800">
          <div
            className="h-1 transition-all duration-500"
            style={{
              width: `${((step + 1) / STEPS.length) * 100}%`,
              backgroundColor: current.iconColor,
            }}
          />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <div className="flex items-center gap-1.5">
            {STEPS.map((s, i) => (
              <button
                key={s.id}
                onClick={() => goToStep(i)}
                className={`rounded-full transition-all ${
                  i === step ? 'w-5 h-2' : 'w-2 h-2'
                }`}
                style={{ backgroundColor: i <= step ? current.iconColor : '#374151' }}
                data-testid={`tour-dot-${i}`}
              />
            ))}
          </div>
          <button
            onClick={finish}
            className="p-1.5 text-gray-500 hover:text-white transition-colors rounded-lg hover:bg-gray-800"
            data-testid="tour-skip-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4">
          {/* Icon + Title */}
          <div className="flex items-start gap-4 mb-4">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: current.iconColor + '20', border: `1px solid ${current.iconColor}30` }}
            >
              <Icon className="w-6 h-6" style={{ color: current.iconColor }} />
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-widest mb-0.5" style={{ color: current.iconColor }}>
                Step {step + 1} of {STEPS.length}
              </p>
              <h2 className="text-lg font-bold text-white leading-tight">{current.title}</h2>
              <p className="text-sm text-gray-400">{current.subtitle}</p>
            </div>
          </div>

          {/* Description */}
          <p className="text-sm text-gray-300 leading-relaxed mb-4">{current.description}</p>

          {/* Steps */}
          {current.steps && (
            <div className="bg-gray-900/50 rounded-xl p-3.5 border border-gray-800/50 mb-4">
              <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-2">How to use it</p>
              <ol className="space-y-1.5">
                {current.steps.map((s, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span
                      className="w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold flex-shrink-0 mt-0.5"
                      style={{ backgroundColor: current.iconColor + '20', color: current.iconColor }}
                    >
                      {i + 1}
                    </span>
                    <span className="text-xs text-gray-400 leading-relaxed">{s}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Tip */}
          <div className="flex items-start gap-2 bg-[#00ff88]/5 border border-[#00ff88]/15 rounded-lg p-2.5">
            <span className="text-[10px] font-bold text-[#00ff88] flex-shrink-0 mt-0.5">TIP</span>
            <p className="text-xs text-gray-400 leading-relaxed">{current.tip}</p>
          </div>
        </div>

        {/* Navigation */}
        <div className="px-5 pb-5 pt-2">
          {/* Never show again checkbox */}
          <label className="flex items-center gap-2 mb-3 cursor-pointer group select-none" data-testid="tour-never-show-label">
            <input
              type="checkbox"
              checked={neverShow}
              onChange={e => setNeverShow(e.target.checked)}
              className="w-3.5 h-3.5 accent-[#00ff88] cursor-pointer"
              data-testid="tour-never-show-checkbox"
            />
            <span className="text-[11px] text-gray-500 group-hover:text-gray-400 transition-colors">
              Don't show me this again
            </span>
          </label>

          <div className="flex items-center justify-between">
            {!isFirst ? (
              <button
                onClick={() => setStep(s => s - 1)}
                className="flex items-center gap-1.5 px-3 py-2 text-gray-400 hover:text-white text-sm font-medium transition-colors"
                data-testid="tour-prev-btn"
              >
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
            ) : (
              <button
                onClick={finish}
                className="text-sm text-gray-600 hover:text-gray-400 transition-colors px-3 py-2"
                data-testid="tour-skip-text-btn"
              >
                Skip tour
              </button>
            )}

            {isLast ? (
              <button
                onClick={finish}
                className="flex items-center gap-2 px-5 py-2 bg-[#00ff88]/20 text-[#00ff88] border border-[#00ff88]/40 rounded-xl text-sm font-bold hover:bg-[#00ff88]/30 transition-all"
                data-testid="tour-done-btn"
              >
                Start Exploring!
              </button>
            ) : (
              <button
                onClick={() => goToStep(step + 1)}
                className="flex items-center gap-2 px-5 py-2 text-white rounded-xl text-sm font-bold hover:opacity-90 transition-all"
                style={{ backgroundColor: current.iconColor + '25', border: `1px solid ${current.iconColor}40` }}
                data-testid="tour-next-btn"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
